import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Loader from '../components/Loader';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import ConfirmationModal from '../components/ConfirmationModal';
import { getErrorMessage } from '../utils/errorHandler';
import { formatDate } from '../utils/dateFormatter';
import { API } from '../config';
import axios from 'axios';
import './BusinessBranches.css';

const BusinessBranches = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success, error } = useNotification();
  const [branches, setBranches] = useState([]);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState(null);
  const [editingBranch, setEditingBranch] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    isActive: true
  });

  useEffect(() => {
    fetchBusiness();
    fetchBranches();
  }, [businessId]);

  const fetchBusiness = async () => {
    try {
      const response = await axios.get(`${API}/businesses/${businessId}`);
      setBusiness(response.data.business);
    } catch (error) {
      console.error('Error fetching business:', error);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/businesses/${businessId}/branches`);
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (branch) => {
    setEditingBranch(branch);
    setFormData({
      name: branch.name,
      description: branch.description || '',
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || '',
      isActive: branch.is_active
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBranch) {
        await axios.put(`${API}/branches/${editingBranch.id}`, formData);
        success('Branch updated successfully!');
      }
      setShowModal(false);
      setEditingBranch(null);
      setFormData({
        name: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        isActive: true
      });
      fetchBranches();
    } catch (err) {
      error(getErrorMessage(err));
    }
  };

  const handleDelete = (branchId) => {
    setBranchToDelete(branchId);
    setShowConfirmModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`${API}/branches/${branchToDelete}`);
      success('Branch deleted successfully!');
      setShowConfirmModal(false);
      setBranchToDelete(null);
      fetchBranches();
    } catch (err) {
      error(getErrorMessage(err));
      setShowConfirmModal(false);
      setBranchToDelete(null);
    }
  };

  return (
    <Layout>
      <div className="business-branches-page">
        <div className="page-header">
          <div>
            <button onClick={() => navigate('/businesses')} className="back-button">
              ← Back
            </button>
            <h1>Branches - {business?.name || 'Loading...'}</h1>
            <p className="page-subtitle">Manage branches for this business</p>
          </div>
        </div>

        {loading ? (
          <Loader message="Loading branches..." variant="block" />
        ) : (
          <div className="table-container">
            {branches.length === 0 ? (
              <div className="empty-state">
                <p>No branches found for this business.</p>
              </div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Address</th>
                    <th>Users</th>
                    <th>Status</th>
                    <th>Created At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {branches.map((branch) => (
                    <tr key={branch.id}>
                      <td className="code-cell">BR-{String(branch.id).padStart(3, '0')}</td>
                      <td className="font-semibold">{branch.name}</td>
                      <td>{branch.email || '—'}</td>
                      <td>{branch.phone || '—'}</td>
                      <td>{branch.address || '—'}</td>
                      <td>
                        <span className="badge">{branch.users?.length || 0}</span>
                      </td>
                      <td>
                        <span className={`status-badge ${branch.is_active ? 'active' : 'inactive'}`}>
                          {branch.is_active ? 'ACTIVE' : 'INACTIVE'}
                        </span>
                      </td>
                      <td className="created-at-cell">{formatDate(branch.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="action-btn edit-btn" 
                            title="Edit"
                            onClick={() => handleEdit(branch)}
                          >
                            ✏️
                          </button>
                          <button 
                            className="action-btn delete-btn" 
                            title="Delete"
                            onClick={() => handleDelete(branch.id)}
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingBranch(null); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>{editingBranch ? 'Edit Branch' : 'Create Branch'}</h2>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Branch Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
                <div className="modal-actions">
                  <button type="button" onClick={() => { setShowModal(false); setEditingBranch(null); }}>Cancel</button>
                  <button type="submit">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => { setShowConfirmModal(false); setBranchToDelete(null); }}
          onConfirm={confirmDelete}
          title="Delete Branch"
          message="Are you sure you want to delete this branch? This will soft delete it."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
        />
      </div>
    </Layout>
  );
};

export default BusinessBranches;
